<template>
    <div class="min-h-screen bg-gray-900 text-white py-3 px-2 sm:py-6 sm:px-4 lg:px-8">
        <div class="max-w-7xl mx-auto">
            <div class="shadow-lg rounded-lg overflow-hidden bg-gray-800 p-3 sm:p-6 hover:shadow-md">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
                    <h2 class="text-xl sm:text-2xl font-semibold text-white">Login Logs & Session Management</h2>
                    <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-4">
                        <button @click="refreshLogs" :disabled="isLoading || isSessionLoading"
                            class="cursor-pointer inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 min-h-[44px] touch-manipulation">
                            <RefreshCw :class="{ 'animate-spin': isLoading || isSessionLoading }"
                                class="h-4 w-4 mr-2" />
                            Refresh
                        </button>
                        <button @click="logoutAllUsers"
                            class="cursor-pointer inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 min-h-[44px] touch-manipulation">
                            <Shield class="h-4 w-4 mr-2" />
                            <span class="hidden sm:inline">Logout All Users</span>
                            <span class="sm:hidden">Logout All</span>
                        </button>
                    </div>
                </div>

                <!-- Tab Navigation -->
                <div class="flex space-x-1 mb-4 sm:mb-6 bg-gray-700 p-1 rounded-lg">
                    <button @click="switchTab('logs')" :class="[
                        'flex-1 text-center py-2.5 sm:py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors min-h-[44px] touch-manipulation',
                        activeTab === 'logs'
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-300 hover:text-white hover:bg-gray-600'
                    ]">
                        <LogIn class="h-4 w-4 inline-block mr-1 sm:mr-2" />
                        <span class="hidden sm:inline">Login History</span>
                        <span class="sm:hidden">History</span>
                    </button>
                    <button @click="switchTab('sessions')" :class="[
                        'flex-1 text-center py-2.5 sm:py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors min-h-[44px] touch-manipulation',
                        activeTab === 'sessions'
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-300 hover:text-white hover:bg-gray-600'
                    ]">
                        <Clock class="h-4 w-4 inline-block mr-1 sm:mr-2" />
                        <span class="hidden sm:inline">Active Sessions</span>
                        <span class="sm:hidden">Sessions</span>
                    </button>
                </div>

                <!-- Statistics Cards - Only show for logs tab -->
                <div v-if="activeTab === 'logs' && stats"
                    class="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8">
                    <div class="bg-gray-700 rounded-lg p-2 sm:p-4">
                        <div class="flex items-center">
                            <LogIn class="h-6 w-6 sm:h-8 sm:w-8 text-blue-400 flex-shrink-0" />
                            <div class="ml-2 sm:ml-4 min-w-0">
                                <p class="text-xs sm:text-sm font-medium text-gray-300 truncate">Total Logins</p>
                                <p class="text-lg sm:text-2xl font-bold text-white">{{ stats.totalLogins }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="bg-gray-700 rounded-lg p-2 sm:p-4">
                        <div class="flex items-center">
                            <Globe class="h-6 w-6 sm:h-8 sm:w-8 text-green-400 flex-shrink-0" />
                            <div class="ml-2 sm:ml-4 min-w-0">
                                <p class="text-xs sm:text-sm font-medium text-gray-300 truncate">Unique IPs</p>
                                <p class="text-lg sm:text-2xl font-bold text-white">{{ stats.uniqueIPs }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="bg-gray-700 rounded-lg p-2 sm:p-4">
                        <div class="flex items-center">
                            <Monitor class="h-6 w-6 sm:h-8 sm:w-8 text-purple-400 flex-shrink-0" />
                            <div class="ml-2 sm:ml-4 min-w-0">
                                <p class="text-xs sm:text-sm font-medium text-gray-300 truncate">Unique Devices</p>
                                <p class="text-lg sm:text-2xl font-bold text-white">{{ stats.uniqueDevices }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="bg-gray-700 rounded-lg p-2 sm:p-4">
                        <div class="flex items-center">
                            <MapPin class="h-6 w-6 sm:h-8 sm:w-8 text-red-400 flex-shrink-0" />
                            <div class="ml-2 sm:ml-4 min-w-0">
                                <p class="text-xs sm:text-sm font-medium text-gray-300 truncate">Countries</p>
                                <p class="text-lg sm:text-2xl font-bold text-white">{{ stats.uniqueCountries }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Quick Stats - Only show for logs tab -->
                <div v-if="activeTab === 'logs' && stats" class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-8">
                    <div class="bg-gray-700 rounded-lg p-3 sm:p-4">
                        <h3 class="text-base sm:text-lg font-medium text-white mb-2 sm:mb-3">Most Common Browser</h3>
                        <p class="text-lg sm:text-xl text-blue-400 truncate">{{ stats.mostCommonBrowser }}</p>
                    </div>

                    <div class="bg-gray-700 rounded-lg p-3 sm:p-4">
                        <h3 class="text-base sm:text-lg font-medium text-white mb-2 sm:mb-3">Most Common Country</h3>
                        <p class="text-lg sm:text-xl text-green-400 truncate">{{ stats.mostCommonCountry }}</p>
                    </div>
                </div>

                <!-- Recent Login Logs -->
                <div v-if="activeTab === 'logs'" class="bg-gray-700 rounded-lg">
                    <div class="px-6 py-4 border-b border-gray-600">
                        <h3 class="text-lg font-medium text-white">Recent Login Activity</h3>
                    </div>

                    <div v-if="isLoading" class="flex justify-center items-center py-8">
                        <RefreshCw class="animate-spin h-6 w-6 text-blue-400" />
                        <span class="ml-2 text-gray-300">Loading...</span>
                    </div>

                    <div v-else-if="logs.length === 0" class="py-8 text-center text-gray-400">
                        No login logs found.
                    </div>

                    <div v-else class="overflow-x-auto -mx-2 sm:mx-0">
                        <table class="min-w-full divide-y divide-gray-600">
                            <thead class="bg-gray-800">
                                <tr>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                                        Device & Browser
                                    </th>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                                        Location
                                    </th>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden xl:table-cell">
                                        Login Time
                                    </th>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                                        IP Address
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-gray-700 divide-y divide-gray-600">
                                <tr v-for="log in logs" :key="log.id" class="hover:bg-gray-600">
                                    <td class="px-2 sm:px-6 py-3 sm:py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                                                <div
                                                    class="h-full w-full rounded-full bg-gray-600 flex items-center justify-center">
                                                    <User class="h-4 w-4 sm:h-5 sm:w-5 text-gray-300" />
                                                </div>
                                            </div>
                                            <div class="ml-2 sm:ml-4 min-w-0 flex-1">
                                                <div class="text-xs sm:text-sm font-medium text-white truncate">
                                                    {{ log.displayName || 'Unknown' }}
                                                </div>
                                                <div class="text-xs sm:text-sm text-gray-400 truncate">
                                                    {{ log.email }}
                                                </div>
                                                <div class="flex items-center mt-1">
                                                    <CheckCircle v-if="log.emailVerified"
                                                        class="h-3 w-3 text-green-400 mr-1 flex-shrink-0" />
                                                    <XCircle v-else class="h-3 w-3 text-red-400 mr-1 flex-shrink-0" />
                                                    <span class="text-xs text-gray-400">
                                                        {{ log.emailVerified ? 'Verified' : 'Unverified' }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-2 sm:px-6 py-3 sm:py-4 hidden lg:table-cell">
                                        <div class="text-xs sm:text-sm text-white">{{ log.deviceInfo?.browser }} on {{
                                            log.deviceInfo?.os }}</div>
                                        <div class="text-xs sm:text-sm text-gray-400">{{ log.deviceInfo?.platform }}</div>
                                        <div class="text-xs text-gray-500">{{ log.deviceInfo?.screenResolution }}</div>
                                    </td>
                                    <td class="px-2 sm:px-6 py-3 sm:py-4 hidden md:table-cell">
                                        <div class="text-xs sm:text-sm text-white truncate">{{ log.locationInfo?.city }}, {{
                                            log.locationInfo?.country }}</div>
                                        <div class="text-xs sm:text-sm text-gray-400 truncate">{{ log.locationInfo?.region }}</div>
                                        <div class="text-xs text-gray-500 truncate">{{ log.locationInfo?.isp }}</div>
                                    </td>
                                    <td class="px-2 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-300 hidden xl:table-cell">
                                        {{ formatDate(log.loginTimestamp) }}
                                    </td>
                                    <td class="px-2 sm:px-6 py-3 sm:py-4 hidden lg:table-cell">
                                        <span
                                            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-600 text-gray-200 truncate max-w-[150px]">
                                            {{ log.locationInfo?.ip }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Active Sessions -->
                <div v-if="activeTab === 'sessions'" class="bg-gray-700 rounded-lg">
                    <div class="px-6 py-4 border-b border-gray-600">
                        <h3 class="text-lg font-medium text-white">Active Sessions</h3>
                        <p class="text-sm text-gray-400 mt-1">
                            Manage active user sessions. You can logout specific sessions or all sessions for a user.
                        </p>
                    </div>

                    <div v-if="isSessionLoading" class="flex justify-center items-center py-8">
                        <RefreshCw class="animate-spin h-6 w-6 text-blue-400" />
                        <span class="ml-2 text-gray-300">Loading sessions...</span>
                    </div>

                    <div v-else-if="activeSessions.length === 0" class="py-8 text-center text-gray-400">
                        No active sessions found.
                    </div>

                    <div v-else class="overflow-x-auto -mx-2 sm:mx-0">
                        <table class="min-w-full divide-y divide-gray-600">
                            <thead class="bg-gray-800">
                                <tr>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                                        Device & Browser
                                    </th>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden xl:table-cell">
                                        Login Time
                                    </th>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                                        Last Activity
                                    </th>
                                    <th
                                        class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-gray-700 divide-y divide-gray-600">
                                <tr v-for="session in activeSessions" :key="session.id" class="hover:bg-gray-600">
                                    <td class="px-2 sm:px-6 py-3 sm:py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                                                <div
                                                    class="h-full w-full rounded-full bg-green-600 flex items-center justify-center">
                                                    <User class="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                                                </div>
                                            </div>
                                            <div class="ml-2 sm:ml-4 min-w-0 flex-1">
                                                <div class="text-xs sm:text-sm font-medium text-white truncate">
                                                    {{ session.displayName || 'Unknown' }}
                                                </div>
                                                <div class="text-xs sm:text-sm text-gray-400 truncate">
                                                    {{ session.email }}
                                                </div>
                                                <div class="flex items-center mt-1">
                                                    <div class="h-2 w-2 bg-green-400 rounded-full mr-2 flex-shrink-0"></div>
                                                    <span class="text-xs text-green-400">Active</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-2 sm:px-6 py-3 sm:py-4 hidden lg:table-cell">
                                        <div class="text-xs sm:text-sm text-white">{{ session.deviceInfo?.browser }} on {{
                                            session.deviceInfo?.os }}</div>
                                        <div class="text-xs sm:text-sm text-gray-400">{{ session.deviceInfo?.platform }}</div>
                                        <div class="text-xs text-gray-500">IP: {{ session.deviceInfo?.ip }}</div>
                                    </td>
                                    <td class="px-2 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-300 hidden xl:table-cell">
                                        {{ formatDate(session.loginTimestamp) }}
                                    </td>
                                    <td class="px-2 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-300 hidden lg:table-cell">
                                        {{ formatDate(session.lastActivity) }}
                                    </td>
                                    <td class="px-2 sm:px-6 py-3 sm:py-4 text-sm font-medium">
                                        <div class="flex flex-col sm:flex-row gap-2">
                                            <button @click="revokeUserSession(session.id)"
                                                class="inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 min-h-[44px] touch-manipulation">
                                                <LogOut class="h-3 w-3 mr-1" />
                                                Logout
                                            </button>
                                            <button @click="revokeAllUserSessions(session.userId)"
                                                class="inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 min-h-[44px] touch-manipulation">
                                                <Shield class="h-3 w-3 mr-1" />
                                                <span class="hidden sm:inline">Logout All</span>
                                                <span class="sm:hidden">All</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Error Toast -->
    <ToastedVue v-if="showToast" :informMessage="toastMessage" :isFailed="toastFailed"
        @onAutoClose="handleCloseToast" />
</template>

<script setup>
import { ref, onMounted } from "vue";
import {
    getRecentLoginLogs,
    getLoginStats
} from "@/composables/useLoginLogs";
import {
    getActiveSessions,
    revokeSession,
    revokeUserSessions,
    revokeAllSessions
} from "@/composables/useSessionManager";
import {
    RefreshCw,
    LogIn,
    Globe,
    Monitor,
    MapPin,
    User,
    CheckCircle,
    XCircle,
    LogOut,
    Shield,
    Clock
} from "lucide-vue-next";
import ToastedVue from "../components/ToastedVue.vue";

const logs = ref([]);
const stats = ref(null);
const activeSessions = ref([]);
const isLoading = ref(false);
const isSessionLoading = ref(false);
const showToast = ref(false);
const toastMessage = ref("");
const toastFailed = ref(false);
const activeTab = ref("logs"); // "logs" or "sessions"

const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown";

    // Handle Firestore timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
};

const loadLoginLogs = async () => {
    try {
        isLoading.value = true;

        // Load recent logs
        const logsResult = await getRecentLoginLogs(null, 50);
        if (logsResult.success) {
            logs.value = logsResult.logs;
        } else {
            throw new Error(logsResult.error);
        }

        // Load statistics
        const statsResult = await getLoginStats();
        if (statsResult.success) {
            stats.value = statsResult.stats;
        } else {
            console.warn("Failed to load stats:", statsResult.error);
        }

    } catch (error) {
        console.error("Error loading login logs:", error);
        showToast.value = true;
        toastMessage.value = `Failed to load login logs: ${error.message}`;
        toastFailed.value = true;
    } finally {
        isLoading.value = false;
    }
};

const loadActiveSessions = async () => {
    try {
        isSessionLoading.value = true;
        const sessionsResult = await getActiveSessions();
        if (sessionsResult.success) {
            activeSessions.value = sessionsResult.sessions;
        } else {
            throw new Error(sessionsResult.error);
        }
    } catch (error) {
        console.error("Error loading active sessions:", error);
        showToast.value = true;
        toastMessage.value = `Failed to load active sessions: ${error.message}`;
        toastFailed.value = true;
    } finally {
        isSessionLoading.value = false;
    }
};

const revokeUserSession = async (sessionId) => {
    if (!confirm("Are you sure you want to logout this session? The user will be signed out immediately.")) {
        return;
    }

    try {
        const result = await revokeSession(sessionId);
        if (result.success) {
            showToast.value = true;
            toastMessage.value = "Session revoked successfully!";
            toastFailed.value = false;
            // Refresh the sessions list
            await loadActiveSessions();
        } else {
            throw new Error(result.error);
        }
    } catch (error) {
        console.error("Error revoking session:", error);
        showToast.value = true;
        toastMessage.value = `Failed to revoke session: ${error.message}`;
        toastFailed.value = true;
    }
};

const revokeAllUserSessions = async (userId) => {
    if (!confirm("Are you sure you want to logout all sessions for this user? They will be signed out from all devices.")) {
        return;
    }

    try {
        const result = await revokeUserSessions(userId);
        if (result.success) {
            showToast.value = true;
            toastMessage.value = `Successfully revoked ${result.revokedCount} session(s)!`;
            toastFailed.value = false;
            // Refresh the sessions list
            await loadActiveSessions();
        } else {
            throw new Error(result.error);
        }
    } catch (error) {
        console.error("Error revoking user sessions:", error);
        showToast.value = true;
        toastMessage.value = `Failed to revoke user sessions: ${error.message}`;
        toastFailed.value = true;
    }
};

const logoutAllUsers = async () => {
    if (!confirm("⚠️ DANGER: This will logout ALL users from ALL devices. Are you absolutely sure?")) {
        return;
    }

    try {
        const result = await revokeAllSessions();
        if (result.success) {
            showToast.value = true;
            toastMessage.value = `Successfully logged out all users! Revoked ${result.revokedCount} session(s).`;
            toastFailed.value = false;
            // Refresh the sessions list
            await loadActiveSessions();
        } else {
            throw new Error(result.error);
        }
    } catch (error) {
        console.error("Error logging out all users:", error);
        showToast.value = true;
        toastMessage.value = `Failed to logout all users: ${error.message}`;
        toastFailed.value = true;
    }
};

const refreshLogs = () => {
    if (activeTab.value === "logs") {
        loadLoginLogs();
    } else {
        loadActiveSessions();
    }
};

const switchTab = (tab) => {
    activeTab.value = tab;
    if (tab === "sessions" && activeSessions.value.length === 0) {
        loadActiveSessions();
    }
};

const handleCloseToast = () => {
    showToast.value = false;
};

onMounted(() => {
    loadLoginLogs();
    loadActiveSessions();
});
</script>
